import React, { useContext, useEffect, useState } from 'react';

import {
  CircleHelpIcon,
  CircleUserIcon,
  LaptopIcon,
  LogOutIcon,
  MoonIcon,
  PlusIcon,
  SunIcon,
  TruckIcon,
} from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore constants is in the parent dir
import DRUMKIT_AUTH_URL from '@constants/DrumkitAuthUrl';

import {
  KitInfoHoverTarget,
  KitPulseTargetView,
  captureKitInfoHoverOnOpen,
  captureKitViewPulseShown,
  getKitViewPulseKey,
  getPosthogCapture,
} from 'components/Kit/EmailSuggestions/utils/posthogEvents';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { SidebarViewContext } from 'contexts/sidebarViewContext';
import { useTheme } from 'contexts/themeContext';
import { useAuth } from 'hooks/useAuth';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { AGENTQ_LOGO } from 'icons/AgentQLogo';
import { DRUMKIT_FULL_NAME_LOGO } from 'icons/DrumkitFullNameLogo';
import { Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import SidebarView from 'types/enums/SidebarView';
import { cn } from 'utils/shadcn';

import { Button } from './Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './Dropdown';
import SearchBar from './SearchBar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';
import { Flex } from './layout';
import { Typography } from './typography';

interface TitlebarButtonsProps {
  outlookLogout?: () => void;
  hideSearchBar?: boolean;
  /** Resolved Drumkit portal base (cluster prefix included). Falls back to DRUMKIT_AUTH_URL. */
  drumkitPortalUrl?: string;
}

const HELP_CENTER_URL = 'https://help.drumkit.ai/';

export function TitlebarButtons({
  outlookLogout,
  hideSearchBar = false,
  drumkitPortalUrl = DRUMKIT_AUTH_URL,
}: TitlebarButtonsProps) {
  const { logout } = useAuth();
  const { currentView, setCurrentView } = useContext(SidebarViewContext);
  const {
    currentState: {
      openExternalUrl,
      threadFreightTrackingIDs,
      hasThreadLoadRelatedSuggestions,
      hasQuoteViewFeatureSuggestions,
      threadId,
    },
  } = useContext(SidebarStateContext);
  const posthog = usePostHog();
  const { theme, toggleTheme } = useTheme();

  const {
    serviceFeaturesEnabled: {
      isCarrierVerificationEnabled,
      isLoadViewEnabled,
      isQuoteViewEnabled,
    },
    tmsIntegrations,
  } = useServiceFeatures();

  // Check if only carrier verification is enabled (no TMS integration)
  const isOnlyCarrierVerificationEnabled =
    isCarrierVerificationEnabled &&
    (!tmsIntegrations || tmsIntegrations.length === 0);

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  // Once per thread + target view; reset when the email thread changes.
  const [shownPulseKeys, setShownPulseKeys] = useState<string[]>([]);

  const logoutHandler = async () => {
    if (outlookLogout) {
      outlookLogout();
    } else {
      openExternalUrl(`${drumkitPortalUrl}/logout`);
      await logout();
    }
  };

  const navigateToPortalHandler = async () => {
    openExternalUrl(drumkitPortalUrl);
  };

  const showViewToggle = isLoadViewEnabled && isQuoteViewEnabled;
  const pulseLoadsWhileOnQuotes =
    currentView === SidebarView.Quote &&
    (threadFreightTrackingIDs.length > 0 || hasThreadLoadRelatedSuggestions);
  const pulseQuotesWhileOnLoads =
    currentView === SidebarView.Loads &&
    hasQuoteViewFeatureSuggestions === true;

  useEffect(() => {
    setShownPulseKeys([]);
  }, [threadId]);

  useEffect(() => {
    if (!showViewToggle) {
      return;
    }

    let targetView: Undef<KitPulseTargetView>;
    if (pulseLoadsWhileOnQuotes) {
      targetView = KitPulseTargetView.Loads;
    } else if (pulseQuotesWhileOnLoads) {
      targetView = KitPulseTargetView.Quote;
    }

    if (!targetView) {
      return;
    }

    const key = getKitViewPulseKey(threadId, targetView);
    if (shownPulseKeys.includes(key)) {
      return;
    }

    setShownPulseKeys((prev) => [...prev, key]);
    captureKitViewPulseShown({
      targetView,
      threadId,
      capture: getPosthogCapture(posthog),
    });
  }, [
    showViewToggle,
    pulseLoadsWhileOnQuotes,
    pulseQuotesWhileOnLoads,
    threadId,
    posthog,
    shownPulseKeys,
  ]);

  const posthogCapture = getPosthogCapture(posthog);

  return (
    <Flex direction='col' gap='sm' className='w-full'>
      <TooltipProvider>
        <Flex
          direction='row'
          justify='between'
          align='center'
          gap='md'
          className='w-full'
        >
          <Flex direction='row' className='w-full'>
            {showViewToggle ? (
              <Flex
                align='center'
                shrink='0'
                className='w-full rounded bg-neutral-200/90 border border-neutral-300 p-0.5'
              >
                <Tooltip
                  delayDuration={500}
                  onOpenChange={(open) =>
                    captureKitInfoHoverOnOpen(open, {
                      capture: posthogCapture,
                      infoTarget: KitInfoHoverTarget.NewTab,
                    })
                  }
                >
                  <TooltipTrigger asChild>
                    <button
                      type='button'
                      className={cn(
                        'relative flex min-h-7 flex-1 items-center justify-center gap-1 rounded px-5 py-1 transition-all',
                        currentView === SidebarView.Quote
                          ? 'cursor-default bg-neutral-50 border border-neutral-300 text-neutral-900 shadow-sm'
                          : 'cursor-pointer text-neutral-600 hover:text-neutral-900'
                      )}
                      onClick={() => {
                        posthog?.capture(ButtonNamePosthog.SwitchToQuoteView, {
                          had_pulse: pulseQuotesWhileOnLoads,
                        });
                        setCurrentView(SidebarView.Quote);
                      }}
                    >
                      <PlusIcon
                        className='h-3.5 w-3.5 shrink-0'
                        strokeWidth={2}
                      />
                      {/* "Quotes" has been renamed to "New", though code still refers to it as Quote View */}
                      <Typography
                        variant='body-xs'
                        weight='medium'
                        className='min-w-0 truncate text-inherit'
                      >
                        New
                      </Typography>
                      {currentView !== SidebarView.Quote &&
                        pulseQuotesWhileOnLoads && (
                          <span
                            aria-hidden
                            className='absolute right-1 top-1/6 h-2 w-2 rounded-full bg-accent-500 shadow-sm animate-pulse'
                          />
                        )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    className='text-neutral-900 text-xs w-[250px]'
                    side='bottom'
                    align='center'
                  >
                    In the New view you'll be able to Quote lanes, Build new
                    loads, and more!
                  </TooltipContent>
                </Tooltip>
                <Tooltip
                  delayDuration={500}
                  onOpenChange={(open) =>
                    captureKitInfoHoverOnOpen(open, {
                      capture: posthogCapture,
                      infoTarget: KitInfoHoverTarget.ActiveTab,
                    })
                  }
                >
                  <TooltipTrigger asChild>
                    <button
                      type='button'
                      className={cn(
                        'relative flex min-h-7 flex-1 items-center justify-center gap-1 rounded px-5 py-1 transition-all',
                        currentView === SidebarView.Loads
                          ? 'cursor-default bg-neutral-50 border border-neutral-300 text-neutral-900 shadow-sm'
                          : 'cursor-pointer text-neutral-600 hover:text-neutral-900'
                      )}
                      onClick={() => {
                        posthog?.capture(ButtonNamePosthog.SwitchToLoadsView, {
                          had_pulse: pulseLoadsWhileOnQuotes,
                        });
                        setCurrentView(SidebarView.Loads);
                      }}
                    >
                      <TruckIcon
                        className='h-3.5 w-3.5 shrink-0'
                        strokeWidth={2}
                      />
                      {/* "Loads" has been renamed to "Active", though code still refers to it as Load View */}
                      <Typography
                        variant='body-xs'
                        weight='medium'
                        className='min-w-0 truncate text-inherit'
                      >
                        Active
                      </Typography>
                      {currentView !== SidebarView.Loads &&
                        pulseLoadsWhileOnQuotes && (
                          <span
                            aria-hidden
                            className='absolute right-1 top-1/6 h-2 w-2 rounded-full bg-accent-500 shadow-sm animate-pulse'
                          />
                        )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    className='text-neutral-900 text-xs w-[250px]'
                    side='bottom'
                    align='center'
                  >
                    In the Active view you'll be able to View and Edit loads,
                    schedule appointments, and more!
                  </TooltipContent>
                </Tooltip>
              </Flex>
            ) : null}
          </Flex>

          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenu
                open={profileDropdownOpen}
                onOpenChange={setProfileDropdownOpen}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.ProfileDropdown}
                    variant='titlebarActionIcon'
                    size='xs'
                    className={cn(
                      profileDropdownOpen && 'bg-brand [&>svg]:stroke-white!'
                    )}
                  >
                    <CircleUserIcon className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent className='w-40 mr-2 rounded-xl'>
                  <DropdownMenuItem
                    onClick={toggleTheme}
                    className='rounded-lg'
                  >
                    {theme === 'light' ? (
                      <>
                        <MoonIcon className='mr-2 h-4 w-4' />
                        Dark Mode
                      </>
                    ) : (
                      <>
                        <SunIcon className='mr-2 h-4 w-4' />
                        Light Mode
                      </>
                    )}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => navigateToPortalHandler()}
                    className='rounded-lg'
                  >
                    <LaptopIcon className='mr-2 h-4 w-4' />
                    <span>Drumkit Portal</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    className='rounded-lg'
                    onClick={() => {
                      posthog?.capture(ButtonNamePosthog.HelpCenterDialog);
                      openExternalUrl(HELP_CENTER_URL);
                    }}
                  >
                    <CircleHelpIcon className='mr-2 h-4 w-4' />
                    <span>Help Center</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => logoutHandler()}
                    className='rounded-lg'
                  >
                    <LogOutIcon className='mr-2 h-4 w-4' />
                    <span>Log Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TooltipTrigger>
            <TooltipContent
              className='border-brand-700'
              side='bottom'
              align='center'
            >
              Profile
            </TooltipContent>
          </Tooltip>
        </Flex>
        {currentView === SidebarView.Loads &&
          !hideSearchBar &&
          !isOnlyCarrierVerificationEnabled && (
            <div className='w-full min-w-0'>
              <SearchBar />
            </div>
          )}
      </TooltipProvider>
    </Flex>
  );
}

export default function Titlebar({ children }: { children: React.ReactNode }) {
  const {
    serviceFeaturesEnabled: { isLeanSolutionsCustomer },
  } = useServiceFeatures();

  return (
    <Flex direction='col'>
      <div className='w-full min-h-12 h-12 flex flex-row items-center px-3 bg-brand-50'>
        <img
          src={isLeanSolutionsCustomer ? AGENTQ_LOGO : DRUMKIT_FULL_NAME_LOGO}
          alt='Drumkit Logo'
          className='h-6'
        />
        <div className='flex-1' />
        <Flex justify='end' gap='md' className='mt-1 pl-3 z- w-full'>
          {/* Render TitlebarButtons in header, assuming it's the first child */}
          {Array.isArray(children) ? children[0] : children}
        </Flex>
      </div>
      <div className='w-full px-3 py-2'>
        {/* Render SearchBar below header, assuming it's the second child */}
        {Array.isArray(children) && children[1]}
      </div>
    </Flex>
  );
}
